import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

// Shape from CE's CAPIFilterSettings_PayrollClasses (apifilters.h):
// enum { selNonUnion=1, selSpecificUnion, selAllUnions, selAll } — selAll
// (default) = Non-Union + All Unions, wire value "all" (live payload verified).
function blankRule() {
  return {
    filter_type: "include",
    prclass: {
      from: "",
      to: "",
      select_type: "all",
      union_number: "",
    },
  };
}

export default function WorkerClassesFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            prclass: {
              from: r.prclass?.from ?? "",
              to: r.prclass?.to ?? "",
              select_type: r.prclass?.select_type ?? "all",
              union_number: r.prclass?.union_number ?? "",
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, prclass: { ...r.prclass, [field]: value } } : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      prclass: {
        from: r.prclass.from || null,
        to: r.prclass.to || null,
        select_type: r.prclass.select_type || "all",
        union_number: r.prclass.union_number || null,
      },
    }));

    onSave({ resource: "prclass", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Worker Classes Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="efm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="efm-fields">
                  <div className="efm-inputs-row">
                    <div className="efm-field">
                      <label>From Class</label>
                      <SearchableCodeSelect
                        value={rule.prclass.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getWorkerClasses}
                        getOptionKey={(option) => option.uuid}
                        formatOptionLabel={(option) =>
                          `${option.code}${
                            option.union ? ` (${option.union})` : ""
                          }${
                            option.description ? ` - ${option.description}` : ""
                          }`
                        }
                        renderOption={(option) => (
                          <>
                            <div className="searchable-code-select__option-code">
                              {option.code}
                              {option.union ? ` (${option.union})` : ""}
                            </div>
                            <div className="searchable-code-select__option-name">
                              {option.description}
                            </div>
                          </>
                        )}
                        emptyMessage="No worker classes found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Class</label>
                      <SearchableCodeSelect
                        value={rule.prclass.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getWorkerClasses}
                        getOptionKey={(option) => option.uuid}
                        formatOptionLabel={(option) =>
                          `${option.code}${
                            option.union ? ` (${option.union})` : ""
                          }${
                            option.description ? ` - ${option.description}` : ""
                          }`
                        }
                        renderOption={(option) => (
                          <>
                            <div className="searchable-code-select__option-code">
                              {option.code}
                              {option.union ? ` (${option.union})` : ""}
                            </div>
                            <div className="searchable-code-select__option-name">
                              {option.description}
                            </div>
                          </>
                        )}
                        emptyMessage="No worker classes found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to", code)}
                      />
                    </div>
                  </div>
                  <div className="efm-inputs-row">
                    <div className="efm-field efm-field--status">
                      <label>Type</label>
                      <select
                        value={rule.prclass.select_type}
                        onChange={(e) =>
                          updateRule(idx, "select_type", e.target.value)
                        }
                      >
                        <option value="all_unions">All Unions</option>
                        <option value="non_union">Non-Union</option>
                        <option value="specific_union">A Specific Union</option>
                        <option value="all">Non-Union + All Unions</option>
                      </select>
                    </div>
                    {rule.prclass.select_type === "specific_union" && (
                      <div className="efm-field">
                        <label>Union</label>
                        <SearchableCodeSelect
                          value={rule.prclass.union_number}
                          companyUuid={companyUuid}
                          fetchOptions={FieldConfigurationAPI.getUnions}
                          formatOptionLabel={(option) =>
                            option.name
                              ? `${option.code} - ${option.name}`
                              : option.code
                          }
                          renderOption={(option) => (
                            <>
                              <div className="searchable-code-select__option-code">
                                {option.code}
                              </div>
                              <div className="searchable-code-select__option-name">
                                {option.name}
                              </div>
                            </>
                          )}
                          emptyMessage="No unions found"
                          disabled={readOnly}
                          onChange={(code) =>
                            updateRule(idx, "union_number", code)
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
